// @ts-check

import { html } from "lit-html";
import { copyTextToClipboard, hasText } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createSortState>} SortState */

const TOOL_FRAME_STYLE =
  "height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 9rem);";
const TOOL_FRAME_CLASS = "flex min-w-0 flex-col gap-3";
const TEXTAREA_LABEL_CLASS =
  "grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-1.5 text-sm font-medium text-stone-700";
const COMPACT_TEXTAREA_CLASS =
  "h-full min-h-0 w-full resize-none rounded-lg border border-stone-300 bg-white px-4 py-3 text-base leading-6 text-stone-900 outline-none transition focus:border-stone-500 focus:ring-2 focus:ring-stone-400/20";
const CONTROL_GRID_CLASS = "mt-3 grid min-w-0 gap-3";
const CHECKBOX_GRID_CLASS = "grid min-w-0 grid-cols-2 gap-2";
const CHECKBOX_CLASS =
  "flex min-w-0 items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-900";
const CHECKBOX_INPUT_CLASS =
  "h-4 w-4 shrink-0 rounded border-stone-400 bg-white text-stone-900 focus:ring-stone-400/30";
const ACTION_GRID_CLASS = "grid gap-2";
const ACTION_ROW_CLASS = "grid grid-cols-2 gap-2";
const ICON_BUTTON_CONTENT_CLASS =
  "inline-flex min-w-0 items-center justify-center gap-1.5";
const COMPACT_PRIMARY_BUTTON_CLASS =
  "inline-flex h-10 min-w-0 items-center justify-center rounded-lg bg-stone-900 px-2 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400";
const COMPACT_SECONDARY_BUTTON_CLASS =
  "inline-flex h-10 min-w-0 items-center justify-center rounded-lg border border-stone-300 bg-white px-2 text-sm font-semibold text-stone-700 transition hover:border-stone-400 hover:bg-stone-50 disabled:cursor-not-allowed disabled:border-stone-200 disabled:bg-white disabled:text-stone-300";

export const sortTool = {
  key: "sort",
  path: "/sort",
  title: "Sort Lines",
  blurb: "Sort newline-separated items, optionally keeping only unique lines.",
  badge: "Text",
  accentClass: "bg-gradient-to-r from-indigo-300 via-sky-300 to-teal-300",
  createState: createSortState,
  render: renderSortPage,
};

function createSortState() {
  return {
    text: "",
    unique: false,
    inverse: false,
    copyStatus: "idle",
  };
}

/**
 * @param {SortState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderSortPage(toolState, env) {
  const canCopy = hasText(toolState.text);
  const copyButtonClass = getCopyButtonClass(toolState.copyStatus);
  const copyButtonLabel = getCopyButtonLabel(toolState.copyStatus);

  return html`
    <section class=${TOOL_FRAME_CLASS} style=${TOOL_FRAME_STYLE}>
      <label class=${TEXTAREA_LABEL_CLASS}>
        <span>Items</span>
        <textarea
          class=${COMPACT_TEXTAREA_CLASS}
          .value=${toolState.text}
          placeholder="Paste one item per line..."
          @input=${
            /**
             * @param {Event} event
             * @returns {void}
             */
            function (event) {
              env.handleFieldInput(toolState, "text", event);
            }
          }
        ></textarea>
      </label>

      <div class=${CONTROL_GRID_CLASS}>
        <div class=${CHECKBOX_GRID_CLASS}>
          ${renderCompactCheckbox({
            label: "Unique",
            checked: toolState.unique,
            field: "unique",
            toolState,
            env,
          })}
          ${renderCompactCheckbox({
            label: "Inverse",
            checked: toolState.inverse,
            field: "inverse",
            toolState,
            env,
          })}
        </div>
        <div class=${ACTION_GRID_CLASS}>
          <button
            class=${COMPACT_PRIMARY_BUTTON_CLASS}
            type="button"
            @click=${function () {
              toolState.text = sortLines(
                toolState.text,
                toolState.unique,
                toolState.inverse,
              );
              toolState.copyStatus = "idle";
              env.scheduleRender();
            }}
          >
            Sort
          </button>
          <div class=${ACTION_ROW_CLASS}>
            <button
              class=${copyButtonClass}
              type="button"
              ?disabled=${!canCopy}
              @click=${async function () {
                await copyText(toolState, env);
              }}
            >
              <span class=${ICON_BUTTON_CONTENT_CLASS}>
                ${renderCopyIcon()}
                <span>${copyButtonLabel}</span>
              </span>
            </button>
            <button
              class=${COMPACT_SECONDARY_BUTTON_CLASS}
              type="button"
              @click=${function () {
                toolState.text = "";
                toolState.copyStatus = "idle";
                env.scheduleRender();
              }}
            >
              <span class=${ICON_BUTTON_CONTENT_CLASS}>
                ${renderClearIcon()}
                <span>Clear</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  `;
}

/**
 * @param {{
 *   label: string,
 *   checked: boolean,
 *   field: "unique" | "inverse",
 *   toolState: SortState,
 *   env: ToolEnv,
 * }} options
 * @returns {LitView}
 */
function renderCompactCheckbox(options) {
  return html`
    <label class=${CHECKBOX_CLASS}>
      <input
        class=${CHECKBOX_INPUT_CLASS}
        type="checkbox"
        ?checked=${options.checked}
        @input=${
          /**
           * @param {Event} event
           * @returns {void}
           */
          function (event) {
            options.env.handleCheckboxInput(
              options.toolState,
              options.field,
              event,
            );
          }
        }
      />
      <span class="min-w-0 truncate">${options.label}</span>
    </label>
  `;
}

/**
 * @returns {LitView}
 */
function renderCopyIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
      />
    </svg>
  `;
}

/**
 * @returns {LitView}
 */
function renderClearIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  `;
}

/**
 * @param {string} text
 * @param {boolean} unique
 * @param {boolean} inverse
 * @returns {string}
 */
function sortLines(text, unique, inverse) {
  const lines = text.split(/\r?\n/).filter(function (line) {
    return line.trim() !== "";
  });

  lines.sort(function (first, second) {
    return first.localeCompare(second, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

  if (inverse) {
    lines.reverse();
  }

  if (!unique) {
    return lines.join("\n");
  }

  const uniqueLines = [];
  const seenLines = new Set();
  for (let i = 0, total = lines.length; i < total; i += 1) {
    const line = lines[i];
    if (seenLines.has(line)) {
      continue;
    }

    seenLines.add(line);
    uniqueLines.push(line);
  }

  return uniqueLines.join("\n");
}

/**
 * @param {SortState} toolState
 * @param {ToolEnv} env
 * @returns {Promise<void>}
 */
async function copyText(toolState, env) {
  if (!hasText(toolState.text)) {
    return;
  }

  try {
    await copyTextToClipboard(toolState.text);
    env.setTemporaryStatus(toolState, "copyStatus", "copied", "idle", "sort");
  } catch (error) {
    console.error("[app] copy sorted text failed", error);
    env.setTemporaryStatus(toolState, "copyStatus", "error", "idle", "sort");
  }
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonClass(copyStatus) {
  let result = COMPACT_SECONDARY_BUTTON_CLASS;
  if (copyStatus === "copied") {
    result =
      "inline-flex h-10 min-w-0 items-center justify-center rounded-lg bg-emerald-700 px-2 text-sm font-semibold text-white";
  }

  if (copyStatus === "error") {
    result =
      "inline-flex h-10 min-w-0 items-center justify-center rounded-lg bg-red-600 px-2 text-sm font-semibold text-white";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonLabel(copyStatus) {
  let result = "Copy";
  if (copyStatus === "copied") {
    result = "Copied";
  }

  if (copyStatus === "error") {
    result = "Copy failed";
  }

  return result;
}
